import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';

import {
  TOAST_MORPH_BLUR_PX,
  TOAST_MORPH_IN_MS,
  TOAST_MORPH_OUT_MS,
  type ToastContentMorphProps,
} from './ToastContentMorph.types';

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

/**
 * WEB content morph — a CSS transition on opacity + filter. On a `morphKey`
 * change the current content transitions to blurred/transparent, the new
 * content is swapped in hidden, and the next frames transition it back to
 * sharp/opaque. Same key = live children (no snapshot), so a progress tick
 * never re-runs the morph. Respects `prefers-reduced-motion` (hard swap).
 */
export function ToastContentMorph({
  morphKey,
  className,
  children,
}: ToastContentMorphProps) {
  const [shown, setShown] = useState<{ key: string; node: ReactNode }>({
    key: morphKey,
    node: children,
  });
  const [phase, setPhase] = useState<'idle' | 'out' | 'in'>('idle');
  // The latest children, read when the swap lands (never re-arms the morph).
  const latest = useRef(children);
  latest.current = children;

  useEffect(() => {
    if (morphKey === shown.key) return undefined;
    if (prefersReducedMotion()) {
      setShown({ key: morphKey, node: latest.current });
      return undefined;
    }
    setPhase('out');
    const timer = setTimeout(() => {
      setShown({ key: morphKey, node: latest.current });
      setPhase('in');
    }, TOAST_MORPH_OUT_MS);
    return () => clearTimeout(timer);
    // Runs on the key edge only; `shown.key` is the previous key by construction.
  }, [morphKey]);

  useEffect(() => {
    if (phase !== 'in') return undefined;
    // Double rAF: paint the hidden state first, then flip so the transition runs.
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setPhase('idle'));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [phase]);

  const hidden = phase !== 'idle';
  const style: CSSProperties = {
    opacity: hidden ? 0 : 1,
    filter: hidden ? `blur(${TOAST_MORPH_BLUR_PX}px)` : 'blur(0px)',
    transition:
      phase === 'in'
        ? 'none'
        : `opacity ${phase === 'out' ? TOAST_MORPH_OUT_MS : TOAST_MORPH_IN_MS}ms ${EASE}, filter ${
            phase === 'out' ? TOAST_MORPH_OUT_MS : TOAST_MORPH_IN_MS
          }ms ${EASE}`,
  };
  // Same key → the live children (progress ticks); mid-morph → the snapshot.
  const node = shown.key === morphKey ? children : shown.node;
  return (
    <div className={className} style={style}>
      {node}
    </div>
  );
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false)
  );
}
